/**
 * @file New-unit coach: wizard creating owner, property, unit, its charge templates and its agreement.
 * Failure path: each save's 400 lands in the coach's own `errorFields` / `errorMessage`, marking the control named by that request-body path.
 */
import * as React from "react";
import Component from "../../AbstractComponent";
import Button from '@material-ui/core/Button';
import TextField from "../Material/TextField";
import Constants from "../../../Action/Constants";
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { AddressSelector } from "../../Partials/AddressSelector/AddressSelector";
import { DatePickerComponent } from "../../Partials/DatePicker/DatePickerComponent";
import CurrencyContainer from "../../../Currency/CurrencyContainer";
import Select from "../../Partials/Material/Select";
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import ReactSelect from 'react-select';
import { PropertyInterface } from "../../../Entity/Entities/Property/PropertyInterface";
import AgencyFeeContainer from "../../../Entity/Entities/AgencyFee/AgencyFeeContainer";
import UnitTypeContainer from "../../../Entity/Entities/Unit/UnitTypeContainer";
import TypeContainer from "../../../Entity/Entities/Charge/Type/TypeContainer";
import { FirstDayOfPeriod } from "../../../Entity/Entities/Charge/PaymentOffset/Offsets/FirstDayOfPeriod";
import PaymentRouteRadio from "../Charge/PaymentRouteRadio";
import { PAYMENT_ROUTE, PaymentRouteId } from "../../../Entity/Entities/Charge/PaymentRoute";
import PeriodContainer from "../../../Entity/Entities/Charge/Period/PeriodContainer";
import Time from "../../../Time/Time";
import Formatter from "../../../Localization/Formatter";
import CompanyLookup from "../../Partials/Lookup/CompanyLookup";
import { CompanyInterface } from "../../../Entity/Entities/Company/CompanyInterface";
import { PageTransition } from "../../Partials/Spinner/PageTransition";
import PropertyLookup from "../../Partials/Lookup/PropertyLookup";
import AssignmentServiceStatusTypeContainer from "../../../Entity/Entities/Unit/ServiceStatus/AssignmentServiceStatusTypeContainer";
import { AgencyFeeInterface } from "Casadesk/Entity/Entities/AgencyFee/AgencyFeeInterface";
// import { ChargeTemplateInterface } from "../../../Entity/Entities/Charge/ChargeTemplateInterface";
import * as uuidV4 from "uuid/v4";

import { NewCoachWrapper } from "./NewCoach/NewCoachWrapper";
import { Title } from "./NewCoach/NewCoachComponents/Title";
import { Subtitle } from "./NewCoach/NewCoachComponents/Subtitle";
import { CoachSummaryTable } from "./NewCoach/NewCoachContents/CoachSummaryTable";
import { LeavePromptDialog } from "../../Partials/Dialog/LeavePromptDialog";

// Request-body path of each coach field → translation key of the label its control shows.
const FIELD_LABELS = {
  companyId: "dropdown-owner-landlord",
  "contacts.firstName": "coach/propertyowner-scenario/person-step/second-field/firstname-label",
  "contacts.lastName": "coach/propertyowner-scenario/person-step/second-field/lastname-label",
  name: "coach/propertyowner-scenario/person-step/third-field/companyname-label",
  "address.googlePlaceId": "property-address",
  complexName: "label-name",
  propertyId: "dropdown-searchforaddress-label",
  unitTypeId: "unit-type",
  unitNumber: "unit-number",
  firstDate: "dialog/agencyfee-field/firstdate-label",
  date: "coach/newagreement/date-agreement",
  fixedFee: "dialog/agencyfee-field/newagency-fixedfee-label",
  percentageFee: "dialog/agencyfee-field/newagency-percentagefee-label",
  serviceStatusId: "page/unitupdate-section/info-changeunitservicestatus/label",
  repeatPeriodId: "dialog/defaultchargetemplates-field/period-label",
  fixedPlusPercentageMode: "dialog/agencyfee-field/fixedpluspercentagemode-label",
  chargeTypeIds: "dialog/agencyfee-field/chargetypes-label",
};
type FieldName = keyof typeof FIELD_LABELS;

type ChargeTemplateRow = {
  amount: any,
  currency: number,
  type: any,
  repeatPeriod: number,
  description: string,
  paymentOffset: any,
  dueDateFirstPayment: any,
  firstDate: any,
  endDate: any,
  isDefault: boolean,
  oldAmount: any,
  contactId: any,
  companyId: any,
  isCollective: boolean,
  editedAmount: string,
  buttonDisabled: boolean,
  isChanged: boolean,
  error: any,
  paymentRouteId: PaymentRouteId,
};

// Key of a sent `chargeTemplates` row → translation key of the label its control shows.
const CHARGE_TEMPLATE_FIELD_LABELS: { [K in keyof ChargeTemplateRow]?: string } = {
  type: "dialog/defaultchargetemplates-field/type-label",
  amount: "label-amount",
  currency: "dialog/defaultchargetemplates-field/currency-label",
  repeatPeriod: "dialog/defaultchargetemplates-field/period-label",
};
type ChargeTemplateFieldName = keyof typeof CHARGE_TEMPLATE_FIELD_LABELS;

// Request-body key of the rows sent with `PUT units/:id`; each row field's path starts with it.
const CHARGE_TEMPLATES_KEY = "chargeTemplates";
const CHARGE_TEMPLATE_PATH_PREFIX = new RegExp(`^${CHARGE_TEMPLATES_KEY}\\[\\d+\\]\\.`);

type FieldProps = { id: string, name: string, className: string, label: string, error: string };

// Types a request-body key against FIELD_LABELS, so a renamed field breaks its body key too.
const bodyKey = <N extends FieldName>(name: N): N => name;
const bodyParentKey = (name: FieldName): string => name.slice(0, name.indexOf("."));
const bodyLeafKey = (name: FieldName): string => name.slice(name.lastIndexOf(".") + 1);

export class CoachNewUnitDialog extends Component {
  public defaultChargeTemplate: ChargeTemplateRow = {
      amount: "",
      currency: this.props.app.settings.user.currency.getCurrencyId(),
      type: TypeContainer.pickIdFromLabel("Basic_rent"),
      repeatPeriod: PeriodContainer.pickIdFromLabel("monthly"),
      description: "",
      paymentOffset: FirstDayOfPeriod.value,
      dueDateFirstPayment: null,
      firstDate: null,
      endDate: null,
      isDefault: true,
      oldAmount: "",
      contactId: null,
      companyId: null,
      isCollective: true,
      editedAmount: "",
      buttonDisabled: true,
      isChanged: false,
      error: null,
      paymentRouteId: PAYMENT_ROUTE.AGENCY,
    }

  public state = {
    activeStep: 0,
    skipped: new Set(),
    openCoachCreateNewUnit: false,
    hasModifiedData: false,
    openLeavePrompt: false,
    disableButtonDuringRequest: false,
    errorFields: null,
    errorMessage: "",
    selectedProperty: null,
    unit: null,
    unitType: UnitTypeContainer.pickIdFromLabel("Apartment"),
    unitNumber: "",
    squareMeters: "",
    roomNumber: "",
    bedroomNumber: "",
    bathroomNumber: "",
    publicationTitle: "",
    amazonFolderName: uuidV4(),
    showNextButton: false,
    isSaving: false,
    isClosePreprocessDialogOpen: false,

    //Owner data
    company: null,
    ownerChoise: "newowner",
    ownerType: "person",
    firstName: "",
    lastName: "",
    companyName: "",
    // disableStep1: false,

    //Property data
    property: null,
    propertyChoise: "newproperty",
    address: {
      formattedAddress: ""
    },

    //Charge template data
    amount: "",
    paymentRouteId: PAYMENT_ROUTE.AGENCY,
    isDefault: true,
    currency: this.props.app.settings.user.currency.getCurrencyId(),
    description: "",
    paymentOffset: FirstDayOfPeriod.value,
    type: TypeContainer.pickIdFromLabel("Basic_rent"),
    dueDateFirstPayment: null,
    dueDateFirstPaymentWasManuallyChanged: false,
    endDate: null,
    isCreateChargeTypePopupOpen: false,
    isCollective: true,
    repeatPeriodWasManuallyChanged: false,
    chargeTemplates: [],
    newChargeTemplates: [{ ...this.defaultChargeTemplate }],
    initialChargeTemplates: [],
    errorAmountTemplate: null,
    chargeTemplatesSuccessfullyCreated: false,

    //Agecy fee data
    fixedFee: "",
    percentageFee: "0",
    fixedPlusPercentageMode: this.props.globalTypes?.FixedPlusPercentageModes?.[this.defaultFixedPlusPercentageModes("FPP_both")],
    vat: "",
    vatPercentage: "",
    firstDate: Time.firstDateCurrentMonth(),
    lastDate: null,
    dateChoice: "firstDate",
    firstDateError: "",
    chargeTypeIds: [TypeContainer.pickIdFromLabel("Basic_rent")],
    objectType: AgencyFeeContainer.pickIdFromLabel("Unit"),
    objectId: null,
    inheritAgencyFee: null,
    changeFixedFeeManual: false,
    changePercentageFeeManual: false,
    isFinishDialogOpen: false,
    repeatPeriod: PeriodContainer.pickIdFromLabel("monthly"),
    assignmentServiceStatus: AssignmentServiceStatusTypeContainer.pickIdFromLabel("OperationalFinancial"),
    dateForAgreement: Time.firstDateNextMonth(),
    createdAgencyFee: null,
    successfullyCreated: false,

    //Complex data
    complexName: "",
    complexUnits: [],
    complexNameAdded: false,
    complexNameError: false,
    nextPropertyForComplex: false,
  };

  constructor(public props) {
    super(props);

  }

  public componentWillMount() {
    const agencyFee = this.props.app.agencyFees.find(f => f.objectId === this.getAgency()?.agencyId && f.removed === false)

    if (agencyFee) {
      const { fixedFee, percentageFee, chargeTypeIds, repeatPeriod, fixedPlusPercentageMode } = agencyFee;
      this.setState({
        fixedFee: String(fixedFee / 100),
        percentageFee: percentageFee,
        chargeTypeIds: chargeTypeIds.length ? chargeTypeIds : [TypeContainer.pickIdFromLabel("Basic_rent")],
        repeatPeriod: repeatPeriod,
        fixedPlusPercentageMode: fixedPlusPercentageMode
      })
    }

    if (this.props.propertyCreateByCoach) {
      this.setState({
        inheritAgencyFee: null,
        selectedProperty: this.props.propertyCreateByCoach,
      });
    }
    if (this.props.company) {
      this.setState({
        company: this.props.company,
        ownerChoise: "existingowner",
        ownerType: "company",
        // disableStep1: true,
        propertyChoise: this.props.company.properties.length ? "existingproperty" : "newproperty"
      })
    }
    if (this.props.property) {
      this.setState({
        company: this.props.property.company,
        ownerChoise: "existingowner",
        ownerType: "company",
        propertyChoise: "existingproperty",
        property: this.props.property,

      })
    }
    if (this.props.activeStep) {
      this.setState({
        activeStep: this.props.activeStep
      })
    }
    this.setState({
      openCoachCreateNewUnit: this.props.openCoachCreateNewUnit,
    });
  }

  public componentWillReceiveProps(nextProps) {
    if (nextProps.propertyCreateByCoach) {
      this.setState({
        inheritAgencyFee: null,
        selectedProperty: nextProps.propertyCreateByCoach
      });
    }
    this.setState({
      openCoachCreateNewUnit: nextProps.openCoachCreateNewUnit,
    });
  }

  public clearState = () => {
    this.setState({
      activeStep: 0,
      skipped: new Set(),
      openCoachCreateNewUnit: false,
      hasModifiedData: false,
      openLeavePrompt: false,
      disableButtonDuringRequest: false,
      errorFields: null,
      errorMessage: "",
      selectedProperty: null,
      unit: null,
      unitType: UnitTypeContainer.pickIdFromLabel("Apartment"),
      unitNumber: "",
      squareMeters: "",
      roomNumber: "",
      bedroomNumber: "",
      bathroomNumber: "",
      publicationTitle: "",
      amazonFolderName: uuidV4(),
      showNextButton: false,
      isSaving: false,
      isClosePreprocessDialogOpen: false,

      //Owner data
      company: null,
      ownerChoise: "newowner",
      ownerType: "person",
      firstName: "",
      lastName: "",
      companyName: "",

      //Property data
      property: null,
      propertyChoise: "newproperty",
      address: {
        formattedAddress: ""
      },

      //Charge template data
      amount: "",
      paymentRouteId: PAYMENT_ROUTE.AGENCY,
      isDefault: !!this.props.shouldCreateDefault,
      currency: this.props.app.settings.user.currency.getCurrencyId(),
      description: "",
      paymentOffset: FirstDayOfPeriod.value,
      type: TypeContainer.pickIdFromLabel("Basic_rent"),
      dueDateFirstPayment: this.props.firstDate ? this.props.firstDate : null,
      dueDateFirstPaymentWasManuallyChanged: false,
      endDate: this.props.endDate ? this.props.endDate : null,
      isCreateChargeTypePopupOpen: false,
      isCollective: true,
      repeatPeriodWasManuallyChanged: false,

      //Agecy fee data
      fixedFee: "",
      percentageFee: "0",
      fixedPlusPercentageMode: this.props.globalTypes?.FixedPlusPercentageModes?.[this.defaultFixedPlusPercentageModes("FPP_both")],
      vat: "",
      vatPercentage: "",
      firstDate: Time.firstDateCurrentMonth(),
      lastDate: null,
      dateChoice: "firstDate",
      firstDateError: "",
      chargeTypeIds: [TypeContainer.pickIdFromLabel("Basic_rent")],
      objectType: AgencyFeeContainer.pickIdFromLabel("Unit"),
      objectId: null,
      inheritAgencyFee: null,
      changeFixedFeeManual: false,
      changePercentageFeeManual: false,
      isFinishDialogOpen: false,
      repeatPeriod: PeriodContainer.pickIdFromLabel("monthly"),
      assignmentServiceStatus: AssignmentServiceStatusTypeContainer.pickIdFromLabel("OperationalFinancial"),
      dateForAgreement: Time.firstDateNextMonth(),
      createdAgencyFee: null,
      successfullyCreated: false,

      //Complex data
      complexName: "",
      complexUnits: [],
      complexNameAdded: false,
      complexNameError: false,
      nextPropertyForComplex: false,
    });
  }

  public clearAgencyFeeState = () => {
    this.setState({
      //Agecy fee data
      fixedFee: "",
      percentageFee: "0",
      fixedPlusPercentageMode: this.props.globalTypes?.FixedPlusPercentageModes?.[this.defaultFixedPlusPercentageModes("FPP_both")],
      vat: "",
      vatPercentage: "",
      firstDate: Time.firstDateCurrentMonth(),
      lastDate: null,
      dateChoice: "firstDate",
      firstDateError: "",
      chargeTypeIds: [TypeContainer.pickIdFromLabel("Basic_rent")],
      objectType: AgencyFeeContainer.pickIdFromLabel("Unit"),
      objectId: null,
      inheritAgencyFee: null,
      changeFixedFeeManual: false,
      changePercentageFeeManual: false,
      repeatPeriod: PeriodContainer.pickIdFromLabel("monthly"),
      assignmentServiceStatus: AssignmentServiceStatusTypeContainer.pickIdFromLabel("OperationalFinancial"),
      dateForAgreement: Time.firstDateNextMonth(),
    });
  }

  componentDidMount() {
    // Assuming FixedPlusPercentageModes is an object with keys as IDs
    const defaultMode = this.props.globalTypes?.FixedPlusPercentageModes['1']; // This is default mode
    this.setState({
      fixedPlusPercentageMode: defaultMode
    });
  }

  public onCloseUnitCoach = () => {
    this.clearBackEndErrors();
    setTimeout(() => { this.clearState() }, 100);

    if (this.props.onCloseUnitCoach) {
      this.props.onCloseUnitCoach.call(this, this.state.unit);
    }
  }

  public plusOneStep = () => {
    const { activeStep } = this.state;
    if (this.state.hasModifiedData && this.state.newChargeTemplates[0]?.editedAmount) {
      this.setState({
        openLeavePrompt: true,
      });
    } else {
      this.setState({
        activeStep: activeStep + 1,
      });
    }
  }

  // Here we save all steps name and get count of steps for showing user steps name.
  public getSteps = () => {
    return [
      this.trans("coach/newunit-step/first-step"),
      this.trans("coach/newunit-step/second-step"),
      this.trans("coach/newunit-step/third-step"),
      this.trans("coach/newunit-step/charge-step"),
      this.trans("coach/newunit-step/fourth-step"),
      this.trans("coach/newunit-step/fifth-step"),
      this.trans("coach/newunit-step/sixth-step"),
    ];
  }

  //All components with content for steps we have here
  public getStepContent = (step) => {
    switch (step) {
      case 0:
        return this.stepContent(step);
      case 1:
        return this.stepContent(step);
      case 2:
        return this.stepContent(step);
      case 3:
        return this.stepContent(step);
      case 4:
        return this.stepContent(step);
      case 5:
        return this.stepContent(step);
      case 6:
        return this.stepContent(step);
      default:
        return this.trans("unknown-step");
    }
  }

  // Content details for diffenrent steps.
  public stepContent = (step) => {
    return (
      <div>
        {step === 0 && this.firstStepDetails()}
        {step === 1 && this.secondStepDetails()}
        {step === 2 && this.thirdStepDetails()}
        {step === 3 && this.chargeStepDetails()}
        {step === 4 && this.forthStepDetails()}
        {step === 5 && this.fifthStepDetails()}
        {step === 6 && this.sixthStepDetails()}
      </div>
    )
  }

  public firstStepSummary = () => {
    let summaryArr = [];
    if (this.state.ownerType === "person") {
      summaryArr.push({
        label: this.trans("coach/propertyowner-field/newperson-label"),
        value: `${this.state.firstName} ${this.state.lastName}`
      });
    }
    summaryArr.push({
      label: this.trans("coach/propertyowner-field/newcompany-label"),
      value: this.state.companyName
    });
    return summaryArr;
  }

  // Steppers components content
  public firstStepDetails = () => {
    if (this.state.company && this.state.ownerChoise === "newowner") {
      return (
        <div className="coach-content-wrapper">
          <Title
            title={this.trans("coach/newunit-step/first-title")}
          />
          <Subtitle
            subtitle={this.trans("coach/general-summary-create-subtitle-dutch-de", { entity: this.trans("label-landlord").toLowerCase() })}
          />
          <CoachSummaryTable
            tableRows={this.firstStepSummary()}
          />
        </div>
      );
    } else {
      const companyId = this.getFieldPropsFor("companyId");
      const firstName = this.getFieldPropsFor("contacts.firstName");
      const lastName = this.getFieldPropsFor("contacts.lastName");
      const companyName = this.getFieldPropsFor("name");

      return (
        <div className="coach-content-wrapper">
          <Title
            title={this.trans("coach/newunit-step/first-title")}
          />
          <Subtitle
            subtitle={this.trans("coach/newunit-step/first-subtitle")}
          />
          <RadioGroup
            name="chargeOwnerChoice"
            className="filters-container"
            value={this.state.ownerChoise}
            onChange={this.changeOwnerChoise}
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              marginBottom: "15px"
            }}
          >
            <FormControlLabel
              control={<Radio color="primary" />}
              value="newowner"
              label={this.trans("coach/newunit-step/first-newowner/label")}
              style={{ width: "auto", fontSize: "16px" }}
            />

            <FormControlLabel
              control={<Radio color="primary" />}
              value="existingowner"
              label={this.trans("coach/newunit-step/first-selectexistingowner/label")}
              style={{ width: "auto", fontSize: "16px" }}
            />
          </RadioGroup>
          {this.state.ownerChoise === "existingowner" ?
            <CompanyLookup
              {...this.props}
              id={companyId.id}
              name={companyId.name}
              className={companyId.className}
              isInlineBlock={true}
              width="100%"
              containerWidth="100%"
              styleInModal={"styleInModal"}
              ref="CompanyLookup"
              floatingLabelText={this.trans("dropdown-searchforlandlord-label")}
              labelText={companyId.label}
              searchText={this.state.company ? this.state.company?.displayName : ""}
              onSelect={this.selectCompany}
              onClear={this.clearCompany}
              noResultText={this.trans("dropdown-owner-notfound")}
              error={companyId.error}
              templateFor={"owner"}
              filterAgency={true}
              hideClickThroughLink={true}
              disableEntityCreation={true}
              autoFocus={this.state.company ? false : true}
            />
            :
            <div>
              <div style={{ maxWidth: "500px" }}>
                <div>
                  {this.trans("coach/newunit-step/first-step-person-or-company")}
                </div>
                <div>
                  <RadioGroup
                    className="filters-container"
                    value={this.state.ownerType}
                    onChange={this.chooseOwnerType}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      width: "500px",
                      paddingLeft: "0px"
                    }}
                  >
                    <FormControlLabel
                      control={<Radio color="primary" />}
                      value="person"
                      label={this.trans("coach/propertyowner-radiobutton/person")}
                      style={{ width: "auto", fontSize: "16px", marginRight: "20px" }}
                    />
                    <FormControlLabel
                      control={<Radio color="primary" />}
                      value="company"
                      label={this.trans("coach/propertyowner-radiobutton/company")}
                      style={{ width: "auto", fontSize: "16px", marginRight: "20px" }}
                    />
                  </RadioGroup>
                </div>
                <div className="hint-styles">
                  {this.trans("coach/newunit-step/first-step-two-or-more-landlords")}
                </div>
                {this.state.ownerType === "person" &&
                  <div>
                    <TextField
                      {...firstName}
                      autoComplete="off"
                      className={`form-element full-size ${firstName.className}`}
                      type="text"
                      value={this.state.firstName}
                      onChange={this.sync.bind(this, this.syncronizers.EVENT, "firstName")}
                      onBlur={this.onBlur}
                      autoFocus={true}
                      variant="standard"
                      error={!!firstName.error}
                    />
                    {!!firstName.error && <div className="component-error-text">{firstName.error}</div>}
                    <TextField
                      {...lastName}
                      autoComplete="off"
                      className={`form-element full-size ${lastName.className}`}
                      type="text"
                      label={lastName.label + this.trans("field/optional")}
                      value={this.state.lastName}
                      onChange={this.sync.bind(this, this.syncronizers.EVENT, "lastName")}
                      onBlur={this.onBlur}
                      variant="standard"
                      error={!!lastName.error}
                    />
                    {!!lastName.error && <div className="component-error-text">{lastName.error}</div>}
                    <TextField
                      {...companyName}
                      autoComplete="off"
                      className={`form-element full-size ${companyName.className}`}
                      type="text"
                      value={this.state.companyName}
                      onChange={this.sync.bind(this, this.syncronizers.EVENT, "companyName")}
                      variant="standard"
                      error={!!companyName.error}
                    />
                    {!!companyName.error && <div className="component-error-text">{companyName.error}</div>}
                    <div className="hint-styles">
                      {this.trans("coach/newunit-step/first-step-company-hint")}
                    </div>
                  </div>
                }
                {this.state.ownerType === "company" &&
                  <div>
                    <TextField
                      {...companyName}
                      autoComplete="off"
                      className={`form-element full-size ${companyName.className}`}
                      type="text"
                      value={this.state.companyName}
                      onChange={this.sync.bind(this, this.syncronizers.EVENT, "companyName")}
                      autoFocus={true}
                      variant="standard"
                      error={!!companyName.error}
                    />
                    {!!companyName.error && <div className="component-error-text">{companyName.error}</div>}
                  </div>
                }
                <div className="coach-new-property-save-button">
                  <Button
                    color="primary"
                    onClick={this.createOwner}
                    variant="contained"
                    style={{ textAlign: "right" }}
                  >
                    {this.trans("button-save")}
                  </Button>
                </div>
              </div>
            </div>
          }
        </div>
      )
    }
  }

  public secondStepSummary = () => {
    let summaryArr = [{
      label: this.trans("title-address"),
      value: this.state.property?.displayName
    }];

    return summaryArr;
  }

  public secondStepDetails = () => {
    if (this.state.property && this.state.propertyChoise === "newproperty") {
      return (
        <div className="coach-content-wrapper">
          <Title
            title={this.trans("coach/newunit-step/second-title")}
          />
          <Subtitle
            subtitle={this.trans("coach/general-summary-create-subtitle-dutch-het", { entity: this.trans("title-address").toLowerCase() })}
          />
          <CoachSummaryTable
            tableRows={this.secondStepSummary()}
          />
        </div>
      );
    } else {
      const address = this.getFieldPropsFor("address.googlePlaceId");
      const complexName = this.getFieldPropsFor("complexName");
      const propertyId = this.getFieldPropsFor("propertyId");

      return (
        <div className="coach-content-wrapper">
          <Title
            title={this.trans("coach/newunit-step/second-title")}
          />
          <Subtitle
            subtitle={this.trans("coach/newunit-step/second-subtitle", { companyDisplayName: this.state.company?.displayName })}
          />
          {
            this.state.ownerChoise === "newowner" ?
              <div>
                <div>
                  <AddressSelector
                    {...this.props}
                    autoFocus={true}
                    id={address.id}
                    name={address.name}
                    className={address.className}
                    styleInModal={"styleInModal"}
                    searchText={this.state?.address?.formattedAddress ? this.state.address.formattedAddress : null}
                    selectedAddress={this.state.address}
                    onSelect={this.sync.bind(this, this.syncronizers.VALUE, "address")}
                    onClear={this.sync.bind(this, this.syncronizers.VALUE, "address", {})}
                    required={true}
                    error={address.error}
                  />
                  <React.Fragment>
                    <TextField
                      id={complexName.id}
                      name={complexName.name}
                      autoComplete="off"
                      className={`form-element full-size ${complexName.className}`}
                      type="text"
                      label={complexName.label}
                      value={this.state.complexName}
                      onChange={this.sync.bind(this, this.syncronizers.EVENT, "complexName")}
                      //autoFocus={true}
                      variant="standard"
                      error={this.state.complexNameError}
                      helperText={this.state.complexNameError && this.trans("be_ajv_not_validated")}
                      disabled={this.state.nextPropertyForComplex}
                    />
                  </React.Fragment>
                  <div className="coach-new-property-save-button">
                    <Button
                      color="primary"
                      onClick={this.createProperty}
                      variant="contained"
                      style={{ textAlign: "right" }}
                    >
                      {this.trans("button-save")}
                    </Button>
                  </div>
                </div>
              </div>
              :
              <div>
                {
                  <RadioGroup
                    name="chargeOwnerChoice"
                    className="filters-container"
                    value={this.state.propertyChoise}
                    onChange={this.changePropertyChoise}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                    }}
                  >
                    <FormControlLabel
                      control={<Radio color="primary" />}
                      value="newproperty"
                      label={this.trans("coach/newunit-step/second-newproperty/label")}
                      style={{ width: "auto", fontSize: "16px" }}
                    />
                    <FormControlLabel
                      control={<Radio color="primary" />}
                      value="existingproperty"
                      label={this.trans("coach/newunit-step/second-selectexistingproperty/label")}
                      style={{ width: "auto", fontSize: "16px" }}
                    />
                  </RadioGroup>
                }
                {this.state.propertyChoise === "existingproperty" ?
                <div>
                  <PropertyLookup
                    {...this.props}
                    id={propertyId.id}
                    name={propertyId.name}
                    ref="PropertyLookup"
                    className={propertyId.className}
                    styleInModal={"styleInModal"}
                    isInlineBlock={true}
                    width="100%"
                    containerWidth="100%"
                    floatingLabelText={propertyId.label}
                    // autoSelectId={this.state.property?.id}
                    searchText={this.state.property ? this.state.property?.displayName : ""}
                    onSelect={this.selectProperty}
                    onClear={this.clearProperty}
                    error={propertyId.error}
                    templateFor={"property"}
                    onlyPropertyRelatedOwner={this.state.company}
                    disableEntityCreation={true}
                    hideClickThroughLink={true}
                    autoFocus={this.state.property ? false : true}
                  />
                  <React.Fragment>
                    <TextField
                      id={complexName.id}
                      name={complexName.name}
                      autoComplete="off"
                      className={`form-element full-size ${complexName.className}`}
                      type="text"
                      label={complexName.label}
                      value={this.state.complexName}
                      onChange={this.sync.bind(this, this.syncronizers.EVENT, "complexName")}
                      //autoFocus={true}
                      variant="standard"
                      error={this.state.complexNameError}
                      helperText={this.state.complexNameError && this.trans("be_ajv_not_validated")}
                      disabled={this.state.nextPropertyForComplex}
                    />
                  </React.Fragment>
                  </div>
                  :
                  <div>
                    <div>
                      <AddressSelector
                        {...this.props}
                        autoFocus={true}
                        id={address.id}
                        name={address.name}
                        className={address.className}
                        styleInModal={"styleInModal"}
                        searchText={this.state?.address?.formattedAddress ? this.state.address.formattedAddress : null}
                        selectedAddress={this.state.address}
                        onSelect={this.sync.bind(this, this.syncronizers.VALUE, "address")}
                        onClear={this.sync.bind(this, this.syncronizers.VALUE, "address", {})}
                        required={true}
                        error={address.error}
                      />
                      <React.Fragment>
                    <TextField
                      id={complexName.id}
                      name={complexName.name}
                      autoComplete="off"
                      className={`form-element full-size ${complexName.className}`}
                      type="text"
                      label={complexName.label}
                      value={this.state.complexName}
                      onChange={this.sync.bind(this, this.syncronizers.EVENT, "complexName")}
                      //autoFocus={true}
                      variant="standard"
                      error={this.state.complexNameError}
                      helperText={this.state.complexNameError && this.trans("be_ajv_not_validated")}
                      disabled={this.state.nextPropertyForComplex}
                    />
                  </React.Fragment>
                      <div className="coach-new-property-save-button">
                        <Button
                          color="primary"
                          onClick={this.createProperty}
                          variant="contained"
                          style={{ textAlign: "right" }}
                        >
                          {this.trans("button-save")}
                        </Button>
                      </div>
                    </div>
                  </div>
                }
              </div>
          }
        </div>
      )
    }
  }

  public chargeStepSummary = () => {
    let summaryArr = [];

    if (this.state.chargeTemplates?.length > 0) {
      this.state.chargeTemplates?.forEach((chargeTemplate, index) => {
        const type = Formatter.checkTranslationType(this.trans(TypeContainer.pickLabelFromId(chargeTemplate.type), {}, "", false), TypeContainer.pickLabelFromId(chargeTemplate.type), chargeTemplate?.type?.system);
        const amount = this.formatter.formatNumber(chargeTemplate.amount * 100, CurrencyContainer.pickFromId(chargeTemplate.currency));
        summaryArr.push({
          label: index === 0 ? this.trans("title-contract-prices") : "",
          value: `${type} ${amount}`
        });
      })
    }

    return summaryArr;
  }

  public chargeStepDetails = () => {
    if (this.state.chargeTemplatesSuccessfullyCreated) {
      return (
        <div className="coach-content-wrapper">
          <Title
            title={this.trans("coach/newunit-step/charge-title")}
          />
          <Subtitle
            subtitle={this.trans("coach/general-summary-create-subtitle-dutch-het", { entity: this.trans("title-unit").toLowerCase() })}
          />
          <CoachSummaryTable
            tableRows={this.chargeStepSummary()}
          />
        </div>
      )
    } else {
      return (
        <div className="coach-content-wrapper" style={{ width: "650px", maxWidth: "unset" }}>
          <Title
            title={this.trans("coach/newunit-step/charge-title")}
          />
          <Subtitle
            subtitle={this.trans("coach/newunit-step/charge-subtitle")}
          />
          {/* <div style={{ display: "flex" }}>
            {this.state.chargeTemplates?.length > 0 &&
              <div>
                <ul style={{ marginTop: "30px" }}>
                  {
                    this.state.chargeTemplates.map((chargeTemplate) => {
                      return <li style={{ position: "relative", display: "flex" }}>
                        {chargeTemplate.error &&
                          <span style={{ position: "absolute", left: "-40px", top: "2px" }}>
                            <span
                              className="icon icon-ic-info red"
                            >
                            </span>
                          </span>
                        }{`${this.formatter.formatNumber(chargeTemplate.amount * 100, CurrencyContainer.pickFromId(chargeTemplate.currency))}, ${Formatter.checkTranslationType(this.trans(TypeContainer.pickLabelFromId(chargeTemplate?.type), {}, "", false), TypeContainer.pickLabelFromId(chargeTemplate?.type), chargeTemplate?.type?.system)}, ${Formatter.checkTranslationType(this.trans(PeriodContainer.pickLabelFromId(chargeTemplate.repeatPeriod)), PeriodContainer.pickLabelFromId(chargeTemplate.repeatPeriod))}`}
                      </li>
                    })
                  }
                </ul>
              </div>
            }

            {this.state.chargeTemplates?.length > 0 &&
              <ul style={{ marginTop: "29px", paddingLeft: "5px" }}>
                {
                  this.state.chargeTemplates.map((chargeTemplate, index) => {
                    return <li style={{ position: "relative", display: "flex" }}>
                      {
                        chargeTemplate?.isPaidDirectlyToOwner && (
                          <div style={{ padding: "0 2px 0 7px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <div className="list-label creation-type-label">
                              {this.trans("label-direct")}
                            </div>
                          </div>

                        )
                      }
                      <span style={{ cursor: "pointer" }} className="icon icon-ic-close clear-button clear-button-list" onClick={this.removeChargeTemplate.bind(this, index)}></span>
                    </li>
                  })
                }
              </ul>
            }
          </div> */}

          <div style={{ display: "flex", flexDirection: "column" }}>
            {this.state.chargeTemplates?.length > 0 &&

              <ul style={{ margin: 0, width: "100%", padding: 0 }}>
                {
                  this.state.chargeTemplates?.map((chargeTemplate, index) => {
                    const typeField = this.getFieldPropsFor("type", index);
                    const amountField = this.getFieldPropsFor("amount", index);
                    const currencyField = this.getFieldPropsFor("currency", index);
                    const repeatPeriodField = this.getFieldPropsFor("repeatPeriod", index);

                    return (
                      <>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "baseline", width: "100%", marginBottom: "10px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "auto", position: "relative" }}>
                            <li style={{ margin: "0", listStyle: "none", fontSize: "16px" }}> {chargeTemplate.error &&
                              <span style={{ position: "absolute", left: "-40px", top: "2px" }}>
                                <span
                                  className="icon icon-ic-info red"
                                >
                                </span>
                              </span>
                            }{`${Formatter.checkTranslationType(this.trans(TypeContainer.pickLabelFromId(chargeTemplate.type), {}, "", false), TypeContainer.pickLabelFromId(chargeTemplate.type), chargeTemplate?.type?.system)}, ${this.formatter.formatNumber(chargeTemplate.oldAmount * 100, CurrencyContainer.pickFromId(chargeTemplate.currency))}${chargeTemplate?.isChanged ? ", " : ""}`}
                              {chargeTemplate.isChanged ? <span style={{ color: "green", fontSize: "16px" }}>{this.trans("title-new")}: {this.formatter.formatNumber(chargeTemplate.amount * 100, CurrencyContainer.pickFromId(chargeTemplate.currency))}</span> : ""}
                            </li>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Select
                                {...typeField}
                                className={`form-element full-size form-input ${typeField.className} correct-label-position`}
                                style={{ minWidth: "180px", marginRight: "16px" }}
                                value={chargeTemplate?.type}
                                onChange={(event) => this.setChargeTemplateType(event, index)}
                                htmlFor={typeField.id}
                                disabled={true}
                              >
                                {
                                  TypeContainer.getChargeTypesForChargeTemplates("contract").map((type, index) => {
                                    return (
                                      <MenuItem key={`chargeType-${index}`} value={type.id} disabled={(type.id === -1 || type.id === -2)}>{`${type.id === -1 || type.id === -2 ? type.label : Formatter.checkTranslationType(this.trans(type?.label, {}, "", type?.system), type?.label, type?.system)}`}</MenuItem>
                                    );
                                  })
                                }
                              </Select>
                              <div className="form-element full-size no-margins">
                                <TextField
                                  {...amountField}
                                  autoComplete="off"
                                  className={`form-element full-size ${amountField.className}`}
                                  type="text"
                                  value={chargeTemplate?.editedAmount}
                                  onChange={(event) => this.setNewChargeAmount(event, index)}
                                  disabled={true}
                                  variant="standard"
                                  error={!!amountField.error}
                                  helperText={amountField.error}
                                />
                              </div>

                              <Select
                                {...currencyField}
                                className={`form-element full-size form-input ${currencyField.className} correct-label-position`}
                                style={{ margin: "0 16px", maxWidth: "90px" }}
                                value={chargeTemplate?.currency}
                                htmlFor={currencyField.id}
                                disabled={true}
                              >
                                {this.getCurrencySelectboxes()}
                              </Select>

                              <Select
                                {...repeatPeriodField}
                                className={`form-element full-size form-input ${repeatPeriodField.className} correct-label-position`}
                                style={{ maxWidth: "110px" }}
                                value={chargeTemplate?.repeatPeriod}
                                onChange={(event) => this.setChargeTemplateRepeatPeriod(event, index)}
                                htmlFor={repeatPeriodField.id}
                                disabled={true}
                              >
                                {this.getRepeatPeriodSelectboxes()}
                              </Select>
                              <Button
                                color="primary"
                                onClick={this.removeChargeTemplate.bind(this, index)}
                                variant="outlined"
                                style={{ marginLeft: "20px", minWidth: "max-content" }}
                              >
                                {this.trans("button-remove")}
                              </Button>
                            </div>
                            {/* <FormControlLabel
                              control={
                                <Checkbox
                                  checked={chargeTemplate?.isPaidDirectlyToOwner}
                                  disabled={true}
                                  color="primary"
                                />
                              }
                              label={this.trans("coach/newcontract-step/paiddirectlytoowner-label")}
                            /> */}
                          </div>
                        </div>

                      </>
                    )
                  })
                }
              </ul>
            }
            {this.state.newChargeTemplates?.length > 0 &&
              <ul style={{ margin: 0, width: "100%", padding: 0 }}>
                {

                  this.state.newChargeTemplates?.map((chargeTemplate, index) => {
                    return (
                      <>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "baseline", width: "100%", marginBottom: "10px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "auto", position: "relative" }}>
                            <li style={{ margin: "0", listStyle: "none", fontSize: "16px" }}> {chargeTemplate.error &&
                              <span style={{ position: "absolute", left: "-40px", top: "2px" }}>
                                <span
                                  className="icon icon-ic-info red"
                                >
                                </span>
                              </span>
                            }{`${Formatter.checkTranslationType(this.trans(TypeContainer.pickLabelFromId(chargeTemplate.type), {}, "", false), TypeContainer.pickLabelFromId(chargeTemplate.type), chargeTemplate?.type?.system)}, ${this.formatter.formatNumber(chargeTemplate.oldAmount * 100, CurrencyContainer.pickFromId(chargeTemplate.currency))}${chargeTemplate?.isChanged ? ", " : ""}`}
                              {chargeTemplate.isChanged ? <span style={{ color: "green", fontSize: "16px" }}>{this.trans("title-new")}: {this.formatter.formatNumber(chargeTemplate.amount * 100, CurrencyContainer.pickFromId(chargeTemplate.currency))}</span> : ""}
                            </li>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <form style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                              <Select
                                className="form-element full-size form-input correct-label-position"
                                style={{ minWidth: "190px", marginRight: "16px" }}
                                error=""
                                value={chargeTemplate?.type}
                                onChange={(event) => this.setChargeTemplateType(event, index)}
                                htmlFor="type"
                                label={this.trans("dialog/defaultchargetemplates-field/type-label")}
                                name="type"
                                id="type"
                              >
                                {
                                  TypeContainer.getChargeTypesForChargeTemplates("contract").map((type, index) => {
                                    return (
                                      <MenuItem key={`chargeType-${index}`} value={type.id} disabled={(type.id === -1 || type.id === -2)}>{`${type.id === -1 || type.id === -2 ? type.label : Formatter.checkTranslationType(this.trans(type?.label, {}, "", type?.system), type?.label, type?.system)}`}</MenuItem>
                                    );
                                  })
                                }
                              </Select>
                              <div className="form-element full-size no-margins">
                                <TextField
                                  autoComplete="off"
                                  className="form-element full-size"
                                  type="text"
                                  label={this.trans("label-amount")}
                                  value={chargeTemplate?.editedAmount}
                                  onChange={(event) => this.setNewChargeAmount(event, index)}
                                  disabled={false}
                                  variant="standard"
                                  error={!!this.state.errorAmountTemplate}
                                  required={true}
                                  style={{ maxWidth: "105px" }}
                                />
                                {this.state.errorAmountTemplate && <div className="component-error-text">{this.trans("be_ajv_not_validated")}</div>}
                              </div>

                              <Select
                                className="form-element full-size form-input correct-label-position"
                                error=""
                                style={{ margin: "0 16px", maxWidth: "100px" }}
                                value={chargeTemplate?.currency}
                                htmlFor="currency"
                                label={this.trans("dialog/defaultchargetemplates-field/currency-label")}
                                name="currency"
                                id="currency"
                                disabled={true}
                              >
                                {this.getCurrencySelectboxes()}
                              </Select>

                              <Select
                                className="form-element full-size form-input correct-label-position"
                                style={{ maxWidth: "110px" }}
                                error=""
                                value={chargeTemplate?.repeatPeriod}
                                onChange={(event) => this.setChargeTemplateRepeatPeriod(event, index)}
                                htmlFor="repeatPeriod"
                                label={this.trans("dialog/defaultchargetemplates-field/period-label")}
                                name="repeatPeriod"
                                id="repeatPeriod"
                              >
                                {this.getRepeatPeriodSelectboxes()}
                              </Select>
                              <Button
                                color="primary"
                                onClick={() => this.saveNewChargeTemplate(index, this.addNewChargeTemplate)}
                                variant="contained"
                                disabled={chargeTemplate?.buttonDisabled}
                                style={{ marginLeft: "20px" }}
                              >
                                {this.trans("button-save")}
                              </Button>
                            </form>
                          </div>
                        </div>
                      </>
                    )
                  })
                }
              </ul>
            }

            {/* {this.state.newChargeTemplates.length === 0 &&
              <Button
                color="primary"
                onClick={this.addNewChargeTemplate}
                variant="contained"
                style={{ width: 'max-content', justifySelf: 'center' }}
              >
                {this.trans("button-add")}
              </Button>
            } */}
            <PaymentRouteRadio
              value={this.state.paymentRouteId}
              onChange={this.setChargeTemplateIsDirectToOwner}
              agencyName={this.getAgency()?.name}
              ownerName={this.state.company?.name || this.state.companyName}
            />
          </div>
        </div>
      );
    }
  }

  public thirdStepSummary = () => {
    let summaryArr = [{
      label: this.trans("title-owner"),
      value: this.state.company?.displayName
    },
    {
      label: this.trans("title-address"),
      value: this.state.unit?.property?.displayName
    },
    {
      label: this.trans("title-unit"),
      value: `${Formatter.checkTranslationType(this.trans(UnitTypeContainer.pickLabelFromId(this.state.unitType)), UnitTypeContainer.pickLabelFromId(this.state.unitType))}`
    }];

    return summaryArr;
  }

  public thirdStepDetails = () => {
    if (this.state.unit) {
      return (
        <div className="coach-content-wrapper" style={{ display: "flex", flexDirection: "column" }}>
          <Title
            title={this.trans("coach/newunit-step/third-title")}
          />
          <Subtitle
            subtitle={this.trans("coach/general-summary-create-subtitle-dutch-het", { entity: this.trans("title-unit").toLowerCase() })}
          />
          <CoachSummaryTable
            tableRows={this.thirdStepSummary()}
          />
        </div>
      );
    } else {
      const unitTypeId = this.getFieldPropsFor("unitTypeId");
      const unitNumber = this.getFieldPropsFor("unitNumber");

      return (
        <div className="coach-content-wrapper">
          <Title
            title={this.trans("coach/newunit-step/third-title")}
          />
          <Subtitle
            subtitle={this.trans("coach/newunit-step/third-subtitle")}
          />
          <Select
            {...unitTypeId}
            className={`form-element full-size form-input ${unitTypeId.className} correct-label-position`}
            value={this.state.unitType}
            onChange={this.sync.bind(this, this.syncronizers.UNIT_TYPE)}
            htmlFor={unitTypeId.id}
          >
            {this.getUnitTypeSelectboxes()}
          </Select>
          {/* <div className="hint-styles">
            {this.trans("coach/newpropertyfirstuser-step/forth-hintforunittype")}
          </div> */}
          <>
           <TextField
              {...unitNumber}
              autoComplete="off"
              className={`form-element full-size ${unitNumber.className}`}
              type="text"
              value={this.state.unitNumber}
              onChange={this.sync.bind(this, this.syncronizers.EVENT, "unitNumber")}
              variant="standard"
              error={!!unitNumber.error}
            />
            {!!unitNumber.error && <div className="component-error-text">{unitNumber.error}</div>}
              <div className="hint-styles">
                {this.trans("coach/newpropertyfirstuser-step/forth-hintforunitnumber")}
              </div>
          </>
          <div className="coach-new-property-save-button">
            <Button
              color="primary"
              onClick={this.createUnit}
              variant="contained"
              style={{ textAlign: "right" }}
            >
              {this.trans("button-save")}
            </Button>
          </div>
        </div>
      )
    }
  }

  public forthStepDetails = () => {
    const firstDate = this.getFieldPropsFor("firstDate");
    const agreementDate = this.getFieldPropsFor("date");
    const fixedFee = this.getFieldPropsFor("fixedFee");
    const percentageFee = this.getFieldPropsFor("percentageFee");
    const serviceStatusId = this.getFieldPropsFor("serviceStatusId");
    const repeatPeriodId = this.getFieldPropsFor("repeatPeriodId");
    const fixedPlusPercentageMode = this.getFieldPropsFor("fixedPlusPercentageMode");

    return (
      <div className="coach-content-wrapper">
        <Title
          title={this.trans("coach/newunit-step/fourth-title")}
        />
        <Subtitle
          subtitle={this.trans("coach/newunit-step/fourth-subtitle")}
        />
        <div
          style={{
            marginRight: "15px",
            color: "gray"
          }}
        >
          {this.trans("dialog/agencyfee-radio/dates-hint")}
        </div>
        <RadioGroup
          name="dateChoice"
          value={this.state.dateChoice}
          onChange={this.changeDateChoice}
          style={{
            display: "flex",
            flexDirection: "row"
          }}
        >
          <FormControlLabel
            value="firstDate"
            control={<Radio color="primary" />}
            label={
              (<span>{this.trans("dialog/agencyfee-field/firstdate-radiobutton")}</span>)}
          />
          {/* <FormControlLabel
            value="contractDate"
            control={<Radio color="primary" />}
            label={
              (<span>{this.trans("dialog/agencyfee-field/startfromcontractdate-label")}</span>)}
          /> */}
        </RadioGroup>

        {
          this.state.dateChoice === "firstDate" && (
            <div style={{ marginBottom: "10px" }}>
              <DatePickerComponent
                {...this.props}
                {...firstDate}
                initialDate={this.state.firstDate}
                onChange={date => this.selectDate(date, "firstDate")}
                clearButton={true}
                width="250px"
                error={firstDate.error || this.state.firstDateError}
              />
            </div>
          )
        }
        <DatePickerComponent
          {...this.props}
          {...agreementDate}
          initialDate={this.state.dateForAgreement}
          onChange={date => this.selectDate(date, "dateForAgreement")}
          clearButton={true}
          width="300px"
        />
        <TextField
          {...fixedFee}
          autoComplete="off"
          className={`form-element form-element ${fixedFee.className}`}
          type="text"
          value={this.state.fixedFee}
          onChange={this.changeFixedFeeAmount}
          style={{ width: "100%" }}
          autoFocus={true}
          variant="standard"
          error={!!fixedFee.error}
          helperText={fixedFee.error}
        />
        <TextField
          {...percentageFee}
          autoComplete="off"
          className={`form-element form-element ${percentageFee.className}`}
          style={{ width: "100%" }}
          type="text"
          value={this.state.percentageFee}
          onChange={this.changePercentageFeeAmount}
          variant="standard"
          error={!!percentageFee.error}
          helperText={percentageFee.error}
        />
        <div className="hint-styles">
          {this.trans("dialog/agencyfee-info/percentagefee")}
        </div>

        <Select
          {...serviceStatusId}
          className={`form-element full-size form-input ${serviceStatusId.className} correct-label-position`}
          value={this.state.assignmentServiceStatus}
          onChange={this.changeAssignmentServiceStatus}
          htmlFor={serviceStatusId.id}
        >
          <MenuItem key={AssignmentServiceStatusTypeContainer.pickIdFromLabel("TenantSearch")} value={AssignmentServiceStatusTypeContainer.pickIdFromLabel("TenantSearch")} className="assignment-service-status-type-el">{this.trans(`assignment_service_status_type_${AssignmentServiceStatusTypeContainer.pickLabelFromId(2).toLowerCase()}`)}</MenuItem>
          <MenuItem key={AssignmentServiceStatusTypeContainer.pickIdFromLabel("OperationalFinancial")} value={AssignmentServiceStatusTypeContainer.pickIdFromLabel("OperationalFinancial")} className="assignment-service-status-type-el">{this.trans(`assignment_service_status_type_${AssignmentServiceStatusTypeContainer.pickLabelFromId(5).toLowerCase()}`)}</MenuItem>
          <MenuItem key={AssignmentServiceStatusTypeContainer.pickIdFromLabel("Financial")} value={AssignmentServiceStatusTypeContainer.pickIdFromLabel("Financial")} className="assignment-service-status-type-el">{this.trans(`assignment_service_status_type_${AssignmentServiceStatusTypeContainer.pickLabelFromId(4).toLowerCase()}`)}</MenuItem>
          <MenuItem key={AssignmentServiceStatusTypeContainer.pickIdFromLabel("Operational")} value={AssignmentServiceStatusTypeContainer.pickIdFromLabel("Operational")} className="assignment-service-status-type-el">{this.trans(`assignment_service_status_type_${AssignmentServiceStatusTypeContainer.pickLabelFromId(3).toLowerCase()}`)}</MenuItem>
        </Select>

        {!!this.formatter.simpleFormatNumber(parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(this.state.percentageFee)).replace(",", ".")).toFixed(2))) && this.renderChargeTypesSelect()}

        <Select
          {...repeatPeriodId}
          className={`form-element full-size form-input ${repeatPeriodId.className} correct-label-position`}
          disabled={!!this.props.disabled || this.state.assignmentServiceStatus === AssignmentServiceStatusTypeContainer.pickIdFromLabel("TenantSearch")}
          value={this.state.repeatPeriod}
          onChange={this.sync.bind(this, this.syncronizers.REPEAT_PERIOD)}
          htmlFor={repeatPeriodId.id}
        >
          {this.getRepeatPeriodAgreementSelectboxes(this.state.assignmentServiceStatus)}
        </Select>

        {!!this.formatter.simpleFormatNumber(parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(this.state.percentageFee)).replace(",", ".")).toFixed(2))) && (
          <Select
            {...fixedPlusPercentageMode}
            className={`form-element full-size form-input ${fixedPlusPercentageMode.className} correct-label-position`}
            value={this.state?.fixedPlusPercentageMode.id}
            onChange={this.updateFPPMode}
            htmlFor={fixedPlusPercentageMode.id}
          >
            {
              Object.keys(this.props.globalTypes?.FixedPlusPercentageModes || {}).map((fpp) => {
                let fpps = this.props.globalTypes?.FixedPlusPercentageModes?.[fpp];

                return (
                  <MenuItem key={fpps.id} value={fpps.id}>{this.formatter.checkTranslationType(this.trans(fpps.label), fpps.label)}</MenuItem>
                );
              })
            }
          </Select>
        )}
      </div>
    );
  }

  public fifthStepSummary = () => {
    let summaryArr = [
      {
        label: this.trans("title-start"),
        value: this.state.dateChoice === "firstDate" ? this.formatter.formatDate(this.state.firstDate) : this.trans("dialog/agencyfee-field/startfromcontractdate-label")
      },
      {
        label: this.trans("dialog/agencyfee-field/newagency-fixedfee-label"),
        value: `${parseFloat((parseFloat(String(Formatter.deleteExtraSpace(String(this.state.fixedFee))).replace(",", "."))).toFixed(2))} ${this.props.app.settings.user.currency.getIso()}`
      },
      {
        label: this.trans("dialog/agencyfee-field/newagency-percentagefee-label"),
        value: `${isNaN(parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(this.state.percentageFee)).replace(",", ".")).toFixed(2))) ? 0 : this.formatter.simpleFormatNumber(parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(this.state.percentageFee)).replace(",", ".")).toFixed(2)), false)}%`
      },
      {
        label: this.trans("page/unitupdate-section/info-changeunitservicestatus/label"),
        value: this.trans(`assignment_service_status_type_${AssignmentServiceStatusTypeContainer.pickLabelFromId(this.state.assignmentServiceStatus).toLowerCase()}`)
      },
      {
        label: this.trans("dialog/defaultchargetemplates-field/period-label"),
        value: this.formatter.checkTranslationType(this.trans(PeriodContainer.pickLabelFromId(this.state?.repeatPeriod)), PeriodContainer.pickLabelFromId(this.state?.repeatPeriod))
      },
      {
        label: this.trans("dialog/agencyfee-field/fixedpluspercentagemode-label"),
        value: this.formatter.checkTranslationType(this.trans(this.state?.fixedPlusPercentageMode?.label), this.state?.fixedPlusPercentageMode?.label)
      }
    ];

    return summaryArr;
  }

  public fifthStepDetails = () => {
    return (
      <div>
        <div className="coach-content-wrapper">
          <Title
            title={this.trans("coach/newunit-step/fifth-title")}
          />
          <Subtitle
            subtitle={this.trans("coach/general-forelast-create-subtitle-dutch-het", { entity: this.trans("title-unit").toLowerCase() })}
          />
          <CoachSummaryTable
            tableRows={this.fifthStepSummary()}
          />
        </div>
      </div>
    );
  }

  public sixthStepDetails = () => {
    return (
      <div>
        <div className="coach-content-wrapper">
          <Title
            title={this.trans("coach/newunit-step/sixth-title")}
          />
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "10px" }}>
            {this.image.getAvatar("success", { size: 62 })}
          </div>
          <Subtitle
            subtitle={this.trans("coach/general-summary-create-subtitle-dutch-het", { entity: this.trans("title-unit").toLowerCase() })}
          />
        </div>
      </div>
    );
  }

  //Funcionality for steps
  public startStep1 = () => {
    if (!this.state.property) {
      this.setState({
        showNextButton: false
      })
    }
    return this.unitStartStepFirst();
  }

  public startStep2 = () => {
    return this.unitStartStepSecond();
  }

  public startStep3 = () => {
    return this.unitStartStepThird();
  }

  public startStep4 = () => {
    return this.unitStartStepChargeTemplate();
  }

  public startStep5 = () => {
    return this.unitStartStepForth();
  }

  public startStep6 = () => {
    return this.unitStartStepFifth();
  }

  //Methods for step logic
  public unitStartStepFirst = () => {
    this.plusOneStep();
  }

  public unitStartStepSecond = () => {
    this.plusOneStep();
  }

  public unitStartStepThird = () => {
    this.plusOneStep();
  }

  public unitStartStepChargeTemplate = () => {
    if (this.state.hasModifiedData && this.state.newChargeTemplates[0]?.editedAmount) {
      this.setState({
        openLeavePrompt: true,
      });
    } else {
      if (!this.state.chargeTemplatesSuccessfullyCreated && this.state.chargeTemplates.length > 0) {
        return this.createDefaultTemplates();
      }
      this.plusOneStep();
    }

  }

  public unitStartStepForth = () => {
    this.plusOneStep();
  }

  public unitStartStepFifth = () => {
    this.createAgreement();
  }

  //Methods
  public handleCreatedProperty = (property: PropertyInterface): void => {
    this.setState({
      selectedProperty: property
    });
  }

  public switchIsPaidDirectlyToOwner = (paymentRouteId: PaymentRouteId) => {
    this.setState({
      paymentRouteId: paymentRouteId
    });
  }

  public defaultFixedPlusPercentageModes(label) {
    const chargeClass = this.props.globalTypes?.FixedPlusPercentageModes;
    for (const key in chargeClass) {
      if (chargeClass[key].label === label) {
        return chargeClass[key].id
      }
    }
  }

  public changeOwnerChoise = (event) => {
    const choice: string = event.target.value;

    this.setState({
      ownerChoise: choice,
      firstName: "",
      showNextButton: false,
      company: null,
      companyName: "",
      lastName: "",
      property: null,
      address: {
        formattedAddress: ""
      },
      unit: null,
      unitNumber: ""
    });
  }

  public changePropertyChoise = (event) => {
    const choice: string = event.target.value;

    this.setState({
      propertyChoise: choice,
      showNextButton: false,
      property: null,
      address: {
        formattedAddress: ""
      },
      unit: null,
      unitNumber: ""
    });
  }

  public openCoachNewContract = () => {
    this.clearBackEndErrors();
    this.clearState();

    if (this.props.onCloseUnitCoach) {
      this.props.onCloseUnitCoach.call(this);
    }
    if (this.props.openCoachNewContract && this.state.unit) {
      this.props.openCoachNewContract.call(this, this.state.unit);
    }
  }

  public changeAssignmentServiceStatus = (event) => {
    const type: number = event.target.value;

    this.setState({
      assignmentServiceStatus: type,
      repeatPeriod: type === AssignmentServiceStatusTypeContainer.pickIdFromLabel("TenantSearch") ? PeriodContainer.pickIdFromLabel("once") : PeriodContainer.pickIdFromLabel("monthly")
    });
  }

  public selectProperty = (property, isManuallySelected) => {
    let inheritAgencyFee;

    if (!isManuallySelected && !property.defaultAgencyFees[0]) {
      this.setState({
        inheritAgencyFee: null
      }, this.clearAgencyFeeState)
      return;
    }
    if (property.defaultAgencyFees[0]) {
      inheritAgencyFee = { ...property.defaultAgencyFees[0].data }
      inheritAgencyFee["objectType"] = AgencyFeeContainer.pickIdFromLabel("Unit");
      inheritAgencyFee["currency"] = 1;
      inheritAgencyFee["fixedPlusPercentageMode"] = property.defaultAgencyFees[0].fixedPlusPercentageMode.id;
      inheritAgencyFee["firstDate"] = Time.formatOrNull(property.defaultAgencyFees[0]?.firstDate);
      inheritAgencyFee["lastDate"] = Time.formatOrNull(property.defaultAgencyFees[0]?.lastDate);
      delete inheritAgencyFee.objectId;
      delete inheritAgencyFee.id;
      if (!inheritAgencyFee.chargeTypeIds) {
        inheritAgencyFee["chargeTypeIds"] = [TypeContainer.pickIdFromLabel("Basic_rent")]
      }

      this.setState({
        fixedPlusPercentageMode: property.defaultAgencyFees[0].fixedPlusPercentageMode,
        fixedFee: this.formatter.formatNumber(property.defaultAgencyFees[0].fixedFee),
        percentageFee: property.defaultAgencyFees[0]?.percentageFee,
        repeatPeriod: property.defaultAgencyFees[0]?.repeatPeriod,
        assignmentServiceStatus: property.defaultAgencyFees[0]?.assignmentServiceStatus,
      })
    }

    this.setState({
      property: property,
      inheritAgencyFee: inheritAgencyFee,
      unit: null,
      unitNumber: ""
    })
  }

  public onChargeTypesSelectChange = (valArray) => {
    const types = !!valArray ? valArray.map(item => item.value) : [];

    this.setState({
      chargeTypeIds: [...types],
      hasModifiedData: true
    });
  }

  public renderChargeTypesSelect = () => {
    const chargeTypeIds = this.getFieldPropsFor("chargeTypeIds");
    const chargeTypes = TypeContainer.getChargeTypesForChargeTemplates("agencyfee")
      .filter(type => {
        if (type.removed) {
          return false;
        }

        if (type.id === -1 || type.id === -2) {
          return false;
        }

        if (TypeContainer.pickIdFromLabel("Stub_receivable") === type.id || TypeContainer.pickIdFromLabel("Stub_payable") === type.id) {
          return false;
        }

        return true;
      })
      .map(type => {
        return ({
          value: type.id,
          label: this.formatter.checkTranslationType(this.trans(type?.label, {}, "", type?.system), type?.label, type?.system)
        });
      }
      );

    const customStyles = {
      control: (base, state) => {
        let borderColor = 'rgba(0, 0, 0, 0.23)';
        let boxShadow = 'none !important';
        let outlineColor = "auto";

        if (state.isFocused) {
          borderColor = "#00BCD4 !important";
          boxShadow = "0 0 1px 1px #00BCD4 !important";
          outlineColor = "#00BCD4 !important";

          if (!!chargeTypeIds.error) {
            borderColor = "#f44336 !important";
            boxShadow = "0 0 1px 1px #f44336 !important";
            outlineColor = "#f44336 !important";
          }
        } else {
          if (!!chargeTypeIds.error) {
            borderColor = "#f44336 !important";
            outlineColor = "#f44336 !important";
          }
        }

        return ({
          ...base,
          border: state.isFocused ? 'none !important' : "auto",
          borderColor: borderColor,
          boxShadow: boxShadow,
          outlineColor: outlineColor,
          outlineStyle: state.isFocused ? "auto !important" : "auto",
          outlineWidth: state.isFocused ? "5px !important" : "auto",
          '&:hover': {
            borderColor: !!chargeTypeIds.error ? '#f44336' : 'black',
          }, // border style on hover
        });
      }
    }

    return (
      <div style={{ position: "relative", zIndex: 2 }}>
        <ReactSelect
          defaultValue={chargeTypes.filter(type => this.state.chargeTypeIds.includes(type.value))}
          isMulti
          options={chargeTypes}
          onChange={this.onChargeTypesSelectChange}
          styles={customStyles}
          id={chargeTypeIds.id}
          name={chargeTypeIds.name}
          placeholder={chargeTypeIds.label}
          className={`form-element ${chargeTypeIds.className}`}
          isDisabled={false}
        />
        {!!chargeTypeIds.error && <div className="component-error-text">{chargeTypeIds.error}</div>}
        <div className="hint-styles-dialog" style={{ margin: "-10px 0 10px 0" }}>
          {this.trans("dialog/agencyfee-info/chargetypes")}
        </div>
      </div>
    );
  }

  public changeFixedFeeAmount = (event): void => {
    let amount: string = event.target.value;
    amount = this.formatter.formatSigns(amount);
    if (this.state.vat) {
      let fixedFee: number = parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(amount)).replace(",", ".")).toFixed(2));
      fixedFee = isNaN(fixedFee) ? 0 : fixedFee;

      let vatNumber: any = parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(this.state.vat)).replace(",", ".")).toFixed(2));
      vatNumber = isNaN(vatNumber) ? 0 : vatNumber;

      let varPercentage = vatNumber / (fixedFee - vatNumber) * 100;

      return this.setState({
        vatPercentage: isFinite(varPercentage) ? this.formatter.simpleFormatNumber(Math.round(varPercentage), false, false) : "",
        fixedFee: amount,
        changeFixedFeeManual: true
      });
    }

    if (this.state.vatPercentage) {
      let fixedFee: number = parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(amount)).replace(",", ".")).toFixed(2));
      fixedFee = isNaN(fixedFee) ? 0 : fixedFee;

      let vatPercentageNumber: any = parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(this.state.vatPercentage)).replace(",", ".")).toFixed(2));
      vatPercentageNumber = isNaN(vatPercentageNumber) ? 0 : vatPercentageNumber;

      const vatNumber = fixedFee / (100 + vatPercentageNumber) * vatPercentageNumber;

      return this.setState({
        vat: this.formatter.simpleFormatNumber(Math.round(vatNumber * 100 + Number.EPSILON) / 100, false),
        fixedFee: amount,
        changeFixedFeeManual: true
      });
    }

    return this.setState({
      vat: "",
      vatPercentage: "",
      fixedFee: amount,
      // hasModifiedData: true
    });
  }

  public updateFPPMode = (event) => {
    const fpp = this.props.globalTypes?.FixedPlusPercentageModes?.[event.target.value];

    this.setState({
      fixedPlusPercentageMode: fpp,
      // hasModifiedData: true
    });
  }

  public changePercentageFeeAmount = (event): void => {
    let amount: string = event.target.value;
    amount = this.formatter.formatSigns(amount);

    this.setState({
      percentageFee: amount,
      changePercentageFeeManual: true
    });
  }

  public clearProperty = (): void => {
    (this.refs.PropertyLookup as any) && (this.refs.PropertyLookup as any).refs.instance.clear();

    this.setState({
      property: null
    });
  }

  public clearBackEndErrors = () => {
    this.setState({
      errorFields: [],
      errorMessage: ""
    });
    this.props.dispatch({
      type: Constants.COACH_ERRORS_CLEAR
    })
  }

  protected getErrorFor(
    field: string,
    errorFields: string[] = this.state.errorFields || [],
    translatedMessage: string = this.state.errorMessage,
    displayName: string = this.getLabelFor(field)
  ): any {
    return super.getErrorFor(field, errorFields, translatedMessage, displayName);
  }

  public getLabelFor = (field: string): string => {
    const labelKey = FIELD_LABELS[field] || CHARGE_TEMPLATE_FIELD_LABELS[field.replace(CHARGE_TEMPLATE_PATH_PREFIX, "")];

    return labelKey ? this.trans(labelKey) : field;
  }

  // The control's `id`, `name`, scroll class, label and backend error, all from its request-body path.
  public getFieldPropsFor(name: FieldName): FieldProps;
  public getFieldPropsFor(name: ChargeTemplateFieldName, rowIndex: number): FieldProps;
  public getFieldPropsFor(name: string, rowIndex?: number): FieldProps {
    const path = rowIndex === undefined ? name : `${CHARGE_TEMPLATES_KEY}[${rowIndex}].${name}`;
    const label = this.getLabelFor(path);

    return {
      id: path,
      name: path,
      className: `scroll-to-${path}`,
      label: label,
      error: this.getErrorFor(path, undefined, undefined, label)
    };
  }

  public clearFieldsError = () => {
    this.setState({
      firstNameError: false,
      companyNameError: false
    })
  }

  public selectCompany = (company: CompanyInterface): void => {
    this.setState({
      company: company,
      property: null,
      address: {
        formattedAddress: ""
      },
      unit: null,
      unitNumber: "",
      propertyChoise: company?.properties.length > 0 ? "existingproperty" : "newproperty"
    });
  }

  public clearCompany = (): void => {
    this.setState({
      company: null
    });
  }

  public changeDateChoice = (event: any): void => {
    const choice: string = event.target.value;

    this.setState({
      dateChoice: choice,
      // hasModifiedData: true
    });
  }

  public selectDate = (date: Date, identifier: string): void => {
    this.setState({
      [identifier]: date,
      // hasModifiedData: true
    })
  }

  public chooseOwnerType = (event) => {
    const choice: string = event.target.value;

    this.setState({
      ownerType: choice,
      companyName: ""
    });
  }

  public onBlur = () => {
    this.setState({
      companyName: this.state.lastName !== "" ?
        this.state.lastName + this.trans("coach/propertyowner-scenario/person-step/third-field/additionalnameofcompany-label")
        :
        this.state.firstName + this.trans("coach/propertyowner-scenario/person-step/third-field/additionalnameofcompany-label"),
      personDisplayName: `${this.state.firstName} ${this.state.lastName}`
    })
  }

  public createOwner = () => {
    const pageTemplate = "owner";
    this.saving(() => {
      this.props.dispatch({
        type: Constants.PAGE_CONTACT_COMPANY_CREATE_START,
        payload: {
          company: {
            [bodyKey("name")]: this.state.companyName,
            email: "",
            phone: "",
            mainBankAccountId: "",
            address: null,
            amazonFolderName: "",
            note: "",
            pageTemplate: pageTemplate,
            [bodyParentKey("contacts.firstName")]: this.state.ownerType === "person" ? {
              ...this.getPayloadForPerson("person")
            } : undefined
          },
          hideSnackbar: true
        },
        onSuccess: (company) => {
          this.setState({
            company: company,
            showNextButton: true,
            errorFields: [],
            errorMessage: "",
            isSaving: false,
            propertyChoise: "newproperty"
          })
        },
        onError: (error) => {
          this.setState({
            errorFields: error.fields,
            errorMessage: error.translatedMessage,
            isSaving: false
          })
        }
      });
    });
  }

  public getPayloadForPerson = (type): any => {
    let pageTemplate = type;
    return {
      [bodyLeafKey("contacts.firstName")]: this.state.firstName,
      [bodyLeafKey("contacts.lastName")]: this.state.lastName,
      profession: "",
      email: "",
      phone1: "",
      phone2: "",
      contactRoleId: null,
      mainBankAccountId: "",
      companyId: null,
      address: {
        formattedAddress: ""
      },
      dateOfBirth: null,
      placeOfBirth: {
        formattedAddress: ""
      },
      identityNumber: "",
      identityType: 255,
      amazonFolderName: "",
      note: "",
      pageTemplate: pageTemplate,
      genderTypeId: 3,
      languageTypeId: this.props.app.settings.agency?.defaultLanguage?.iso === 'en-US' ? 1 : 2
    };
  }

  public createProperty = () => {
    this.saving(() => {
      this.props.dispatch({
        type: Constants.PAGE_PROPERTY_CREATE_START,
        payload: {
          property: {
            [bodyKey("complexName")]: this.state.complexName,
            [bodyParentKey("address.googlePlaceId")]: { ...this.state.address },
            [bodyKey("companyId")]: this.state.company?.id,
            managerId: null,
            amazonFolderName: this.state.amazonFolderName,
            note: "",
            isDefaultUnit: false,
            chargeTemplatesIds: null,
            contactInformationOfAssociation: "",
            associationOwnerId: null
          },
          hideSnackbar: true
        },
        onSuccess: (property: PropertyInterface) => {
          this.setState({
            property: property,
            errorFields: [],
            errorMessage: "",
            isSaving: false
          })
        },
        onError: (err) => {
          this.setState({
            isSaving: false,
            errorFields: err.fields,
            errorMessage: err.translatedMessage
          });
        }
      });
    });
  }

  public createUnit = () => {
    let unit = {
      [bodyKey("propertyId")]: this.state.property?.id,
      [bodyKey("unitNumber")]: this.state.unitNumber,
      [bodyKey("unitTypeId")]: this.state.unitType,
      squareMeters: this.convertToInteger(this.state.squareMeters),
      roomNumber: this.convertToInteger(this.state.roomNumber),
      bedroomNumber: this.convertToInteger(this.state.bedroomNumber),
      bathroomNumber: this.convertToInteger(this.state.bathroomNumber),
      publicationTitle: this.state.publicationTitle,
      amazonFolderName: this.state.amazonFolderName
    }

    this.saving(() => {
      this.props.dispatch({
        type: Constants.GLOBAL_UNIT_CREATE_START,
        payload: {
          unit: unit,
          hideSnackbar: true
        },
        onSuccess: unit => {
          return this.setState({
            unit: unit,
            errorFields: [],
            errorMessage: "",
            isSaving: false
          });
        },
        onError: err => {
          this.setState({
            errorFields: err.fields,
            errorMessage: err.translatedMessage,
            isSaving: false
          })
        }
      });
    });
  }

  public createDefaultTemplates = () => {
    const defaultTemplates = this.state.chargeTemplates?.map((template) => {
      let updatedTemplate = {
        ...template,
        unitId: this.state.unit?.id,
        paymentRouteId: this.state.paymentRouteId

      }
      // Remove unnecessary properties before sending to back end.
      delete updatedTemplate?.oldAmount;
      delete updatedTemplate?.editedAmount;
      delete updatedTemplate?.buttonDisabled;
      delete updatedTemplate?.percentage;
      delete updatedTemplate?.isChanged;

      return updatedTemplate
    })

    this.saving(() => {
      this.props.dispatch({
        type: Constants.GLOBAL_UNIT_UPDATE_START,
        payload: {
          id: this.state.unit && this.state.unit?.id,
          unit: {
            [CHARGE_TEMPLATES_KEY]: defaultTemplates
          },
          hideSnackbar: true
        },
        onSuccess: unit => {
          return this.setState({
            unit: unit,
            errorFields: [],
            errorMessage: "",
            isSaving: false,
            chargeTemplatesSuccessfullyCreated: true
          });
        },
        onError: err => {
          this.setState({
            errorFields: err.fields,
            errorMessage: err.translatedMessage,
            isSaving: false,
            chargeTemplatesSuccessfullyCreated: false
          })
        }
      });
    });
  }

  public createAgreement = () => {
    let agencyFeeTemplates
    if (!this.state.inheritAgencyFee) {
      agencyFeeTemplates = {
        objectType: this.state.objectType,
        currency: this.state.currency,
        [bodyKey("fixedFee")]: parseFloat((parseFloat(String(Formatter.deleteExtraSpace(String(this.state.fixedFee))).replace(",", "."))).toFixed(2)),
        [bodyKey("fixedPlusPercentageMode")]: this.state.fixedPlusPercentageMode?.id,
        [bodyKey("percentageFee")]: parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(this.state.percentageFee)).replace(",", ".")).toFixed(2)),
        vat: parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(this.state.vat)).replace(",", ".")).toFixed(2)),
        [bodyKey("firstDate")]: this.state.dateChoice === "firstDate" ? Time.formatOrNull(this.state.firstDate) : null,
        lastDate: Time.formatOrNull(this.state.lastDate),
        [bodyKey("chargeTypeIds")]: this.state.chargeTypeIds,
        [bodyKey("repeatPeriodId")]: this.state.repeatPeriod,
        [bodyKey("serviceStatusId")]: this.state.assignmentServiceStatus,
        [bodyKey("date")]: Time.formatOrNull(this.state.dateForAgreement),
        companyId: this.state.company.id,
      }
    } else {
      agencyFeeTemplates = this.state.inheritAgencyFee
      agencyFeeTemplates[bodyKey("fixedPlusPercentageMode")] = this.state.fixedPlusPercentageMode?.id;
      agencyFeeTemplates[bodyKey("fixedFee")] = parseFloat((parseFloat(String(Formatter.deleteExtraSpace(String(this.state.fixedFee))).replace(",", "."))).toFixed(2));
      agencyFeeTemplates[bodyKey("percentageFee")] = parseFloat(parseFloat(String(this.formatter.deleteExtraSpace(this.state.percentageFee)).replace(",", ".")).toFixed(2));
      agencyFeeTemplates[bodyKey("repeatPeriodId")] = this.state.repeatPeriod;
      agencyFeeTemplates[bodyKey("serviceStatusId")] = this.state.assignmentServiceStatus;
      agencyFeeTemplates[bodyKey("date")] = Time.formatOrNull(this.state.dateForAgreement);
    }

    if (agencyFeeTemplates) {
      agencyFeeTemplates.objectId = this.state.unit?.id;
    }

    this.saving(() => {
      this.props.dispatch({
        type: Constants.GLOBAL_AGENCY_FEE_CREATE_START,
        payload: {
          ...agencyFeeTemplates,
          hideSnackbar: true
        },
        onSuccess: (agencyFee: AgencyFeeInterface) => {
          return this.setState({
            errorFields: [],
            errorMessage: "",
            showNextButton: true,
            isSaving: false,
            createdAgencyFee: agencyFee,
            activeStep: 6
          });
        },
        onError: err => {
          this.setState({
            errorFields: err.fields,
            errorMessage: err.translatedMessage,
            isSaving: false,
            activeStep: 4
          })
        }
      });
    });
  }

  public closePreprocessDialogOpen = () => {
    this.setState({
      isClosePreprocessDialogOpen: false
    });
  }

  public removeChargeTemplate = (index: number): void => {
    const chargeTemplates = [...this.state.chargeTemplates];
    const removedCharge = chargeTemplates.splice(index, 1)[0];
    const editedInitialChargeTemplates = this.state.initialChargeTemplates?.map(chargeTemplate => {
      if (removedCharge.id === chargeTemplate.id) {
        chargeTemplate.amount = 0
      }
      return chargeTemplate
    })

    // The 400's row paths name the rows as they were sent, so a removal makes them stale.
    this.setState({
      chargeTemplates: [...chargeTemplates],
      // hasModifiedData: true,
      initialChargeTemplates: [...editedInitialChargeTemplates],
      errorFields: [],
      errorMessage: ""
    });
  }

  public updatePrice = (id) => {
    const newTemplateArr = this.state.chargeTemplates?.map(template => {
      if (template?.id === id) {
        template.isChanged = true;
        template.amount = parseFloat(parseFloat(String(Formatter.deleteExtraSpace(template.editedAmount)).replace(",", ".")).toFixed(2));
        template.buttonDisabled = true;
      }
      return template
    })

    this.setState({
      disableNextButton: false,
      chargeTemplates: newTemplateArr,
      hasModifiedData: true,
    })

  }

  public changePriceNewAmount = (event, id): void => {
    const newAmount: string = this.formatter.formatSigns(event.target.value);
    const newTemplateArr = this.state.chargeTemplates?.map(template => {
      if (template?.id === id) {
        template.editedAmount = newAmount;
        template.buttonDisabled = false;
      }
      return template
    })
    this.setState({
      chargeTemplates: newTemplateArr,
      hasModifiedData: true,
    });
  }


  public removeNewChargeTemplate = (index: number): void => {
    const chargeTemplates = [...this.state.newChargeTemplates];
    chargeTemplates.splice(index, 1);

    this.setState({
      newChargeTemplates: [...chargeTemplates],
      // hasModifiedData: true
    });
  }

  public addNewChargeTemplate = (): void => {
    const chargeTemplates = [...this.state.newChargeTemplates];

    let chargeTemplate = {
      amount: "",
      currency: this.props.app.settings.user.currency.getCurrencyId(),
      type: TypeContainer.pickIdFromLabel("Other_receivable"),
      repeatPeriod: PeriodContainer.pickIdFromLabel("monthly"),
      description: "",
      paymentOffset: FirstDayOfPeriod.value,
      dueDateFirstPayment: null,
      firstDate: null,
      endDate: null,
      isDefault: true,
      contactId: null,
      companyId: null,
      isCollective: true,
      oldAmount: "",
      editedAmount: "",
      buttonDisabled: true,
      isChanged: false,
      error: null,
      paymentRouteId: PAYMENT_ROUTE.AGENCY,
    }

    chargeTemplates.push(chargeTemplate);

    this.setState({ hasModifiedData: true, newChargeTemplates: [...chargeTemplates] });
  }
  public modified = () => {
    this.setState({ hasModifiedData: true })
  }
  public setNewChargeAmount = (event, index: number): void => {
    const amount: string = this.formatter.formatSigns(event.target.value);

    this.modified()
    this.setState({
      newChargeTemplates: this.state.newChargeTemplates.map((template, ind) => {
        if (index === ind) {
          return {
            ...template,
            editedAmount: amount,
            buttonDisabled: false
          }
        }
        return template
      })
    })
  }

  public setChargeTemplateType = (event, index: number): void => {
    const type = event.target.value;
    this.setState({
      newChargeTemplates: this.state.newChargeTemplates.map((template, ind) => {
        if (index === ind) {
          return {
            ...template,
            type: type,
            isCollective: type === TypeContainer.pickIdFromLabel("Deposit") ? false : true,
            repeatPeriod: type === TypeContainer.pickIdFromLabel("Deposit") ? PeriodContainer.pickIdFromLabel("once") : PeriodContainer.pickIdFromLabel("monthly"),
          }
        }
        return template
      })
    })
  }

  public setChargeTemplateIsDirectToOwner = (paymentRouteId: PaymentRouteId): void => {
    this.switchIsPaidDirectlyToOwner(paymentRouteId)
    this.setState({
      newChargeTemplates: this.state.newChargeTemplates.map((template) => {

        return {
          ...template,
          paymentRouteId: paymentRouteId
        }
      })
    })
  }

  public setChargeTemplateRepeatPeriod = (event, index: number): void => {
    const repeatPeriod = event.target.value;
    this.setState({
      newChargeTemplates: this.state.newChargeTemplates.map((template, ind) => {
        if (index === ind) {
          const res = {
            ...template,
            repeatPeriod: repeatPeriod,
            isCollective: template.type === TypeContainer.pickIdFromLabel("Deposit") ? false : true
          }
          return res
        }
        return template
      })
    })
  }

  public saveNewChargeTemplate = (index: number, callback = () => { }): void => {

    const chargeTemplate = this.state.newChargeTemplates[index];
    if (chargeTemplate.editedAmount === "" || !chargeTemplate.editedAmount?.trim()?.length) {
      return this.setState({
        errorAmountTemplate: true
      });
    }
    chargeTemplate.amount = parseFloat(parseFloat(String(Formatter.deleteExtraSpace(chargeTemplate.editedAmount)).replace(",", ".")).toFixed(2));
    chargeTemplate.oldAmount = parseFloat(parseFloat(String(Formatter.deleteExtraSpace(chargeTemplate.editedAmount)).replace(",", ".")).toFixed(2));
    chargeTemplate.buttonDisabled = true;
    chargeTemplate.paymentRouteId = this.state.paymentRouteId;
    this.setState({
      newChargeTemplates: this.state.newChargeTemplates.filter((_template, ind) => ind !== index),
      chargeTemplates: [...this.state.chargeTemplates, chargeTemplate],
      disableNextButton: false,
      openLeavePrompt: false,
      hasModifiedData: false,
      errorAmountTemplate: false,
    }, callback)
  }

  public render() {
    const steps = this.getSteps();
    return (
      <div>
        <NewCoachWrapper
          state={this.state}
          steps={steps}
          oldCoach={true}
          content={(activeStep) => this.getStepContent(activeStep)}
          openCoach={this.state.openCoachCreateNewUnit}
          onCloseCoach={this.onCloseUnitCoach}
          onPageChange={(activeStep, isNext) => {
            isNext && this._handlePageChange(activeStep);
            !isNext && this.setState({ activeStep, isDisabled: false })
          }}
          showBackButton={this.state.activeStep !== steps.length - 1}
          disabledNext={
            (this.state.activeStep === 0 && !this.state.company) ||
            (this.state.activeStep === 1 && !this.state.property) ||
            (this.state.activeStep === 2 && !this.state.unit) ||
            (this.state.activeStep === 4 && !this.state.fixedFee && !this.state.percentageFee)
          }
        />

        <LeavePromptDialog
          {...this.props}
          prioritizeClosing={true}
          onClose={() => this.setState({ openLeavePrompt: false, nextPath: "" })}
          openLeavePrompt={this.state.openLeavePrompt}
          content={this.trans("warning-templates-may-be-lost")}
          onDiscard={() => this.setState({ openLeavePrompt: false, hasModifiedData: false }, this.createDefaultTemplates)}
          onSave={() => { this.setState({ openLeavePrompt: false, hasModifiedData: false }, () => { this.saveNewChargeTemplate(this.state.newChargeTemplates.length - 1, this.createDefaultTemplates) }) }}
          hideCross={true}
          leavePrompt={true}
          customButtons={<div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <Button
                variant="contained"
                color="primary"
                onClick={() => { this.setState({ openLeavePrompt: false, hasModifiedData: false }, () => { this.createDefaultTemplates() }) }}
              >
                {this.trans("button-continue")}
              </Button>
              <Button
                color="primary"
                onClick={() => this.setState({ openLeavePrompt: false })}
              >
                {this.trans("button-cancel")}
              </Button>

            </div>
          </div>}
        />
        <PageTransition isSaving={this.state.isSaving} />
      </div>
    );
  }
}
